import type {
  NoyauCode,
  ReviewCertainty,
  ReviewExtract,
  ReviewGrid,
  ReviewNoyau,
  ReviewOperation,
} from '@matter_extractor/shared';
import { crossPlacements, isDoubtLocked, reviewNoyauProblem } from '@matter_extractor/shared';
import { caseNumber } from './review-transcript';

/**
 * What a GESTURE of the review page sends, and what it says before it is made
 * (relecture.md §5, §8) - pure, so the rules the page draws are the ones
 * `shared` already runs on the server.
 *
 * Nothing here decides what the server will accept: `extractsOverlap`,
 * `crossPlacements`, `isDoubtLocked` and `reviewNoyauProblem` are the shared
 * rules and are IMPORTED. What is written here is the page's own arithmetic -
 * which options a list offers, which sentence goes with them.
 */

/* ------------------------------------------------------------------ the passage */

/** A passage of a reply, as a gesture carries it: its turn (1-based) and its position. */
export interface PassageTarget {
  readonly turn: number;
  readonly start: number;
  readonly end: number;
  readonly text: string;
}

/** « Ranger ce passage dans cette case » - the `range` operation of the contract. */
export function rangeOperation(
  objectiveId: string,
  target: PassageTarget,
): Extract<ReviewOperation, { op: 'range' }> {
  return {
    op: 'range',
    objectiveId,
    turn: target.turn,
    start: target.start,
    end: target.end,
    text: target.text,
  };
}

/** One option of the list of cases a passage can go into (§5.2, §5.4). */
export interface CaseOption {
  readonly objectiveId: string;
  readonly number: number;
  readonly name: string;
  /** Putting it here puts a « ? » on both placements (§5.4). */
  readonly withDoubt: boolean;
  /** The case already holds this very passage: ranging it again would change nothing. */
  readonly taken: boolean;
}

/** What the list of cases says about a passage before one is chosen. */
export interface PlacementPreview {
  readonly options: readonly CaseOption[];
  /** The cases that already hold a passage overlapping this one - the warning of §5.4. */
  readonly sharedWith: readonly { readonly number: number; readonly name: string }[];
  /** Under the setting, the warning is shown. */
  readonly warns: boolean;
}

/**
 * The list a selection opens (§5.2) and the warning §5.4 puts above it.
 *
 * Under « un passage, un seul objectif », a case other than those that already
 * hold an overlapping passage is offered « avec doute », and the list warns.
 * Unticked, the list is plain: a passage may go in two cases freely (CR 20).
 * A case whose extract already COVERS the whole selection is `taken` - the
 * list shows it and refuses it, since the gesture would change nothing.
 */
export function placementPreview(
  target: PassageTarget,
  extracts: readonly ReviewExtract[],
  grid: Pick<ReviewGrid, 'objectives' | 'oneObjectivePerPassage'>,
): PlacementPreview {
  const overlapping = extracts.filter(
    (extract) =>
      extract.turn === target.turn && extract.start < target.end && target.start < extract.end,
  );
  const holders = new Set(overlapping.map((extract) => extract.objectiveId));
  const covering = new Set(
    overlapping
      .filter((extract) => extract.start <= target.start && extract.end >= target.end)
      .map((extract) => extract.objectiveId),
  );
  const order = grid.objectives.map((objective) => objective.objectiveId);
  const options = grid.objectives.map<CaseOption>((objective) => ({
    objectiveId: objective.objectiveId,
    number: caseNumber(order, objective.objectiveId),
    name: objective.name,
    withDoubt:
      grid.oneObjectivePerPassage && holders.size > 0 && !holders.has(objective.objectiveId),
    taken: covering.has(objective.objectiveId),
  }));
  const sharedWith = options
    .filter((option) => holders.has(option.objectiveId))
    .map(({ number, name }) => ({ number, name }));
  return { options, sharedWith, warns: grid.oneObjectivePerPassage && sharedWith.length > 0 };
}

/** The cases a passage already placed could ALSO go into, given what holds it. */
export function otherCases(
  grid: Pick<ReviewGrid, 'objectives'>,
  placed: readonly ReviewExtract[],
): { readonly objectiveId: string; readonly number: number; readonly name: string }[] {
  const held = new Set(placed.map((extract) => extract.objectiveId));
  const order = grid.objectives.map((objective) => objective.objectiveId);
  return grid.objectives
    .filter((objective) => !held.has(objective.objectiveId))
    .map((objective) => ({
      objectiveId: objective.objectiveId,
      number: caseNumber(order, objective.objectiveId),
      name: objective.name,
    }));
}

/* ------------------------------------------------------------------- the doubt */

/** What the « ? » of an extract is, and why it may not move (§5.4, D28). */
export interface DoubtState {
  readonly locked: boolean;
  /** The numbers of the OTHER cases that hold the same passage. */
  readonly alsoIn: readonly number[];
}

export function doubtState(
  extract: ReviewExtract,
  all: readonly ReviewExtract[],
  grid: Pick<ReviewGrid, 'objectives' | 'oneObjectivePerPassage'>,
): DoubtState {
  const order = grid.objectives.map((objective) => objective.objectiveId);
  const locked = isDoubtLocked(extract, all, grid);
  const alsoIn = locked
    ? [
        ...new Set(
          crossPlacements(extract, all).map((other) => caseNumber(order, other.objectiveId)),
        ),
      ]
        .filter((number) => number > 0)
        .sort((a, b) => a - b)
    : [];
  return { locked, alsoIn };
}

/** « aussi en 6 : retirez l’un des deux pour lever le doute » (§5.4). */
export function lockSentence(alsoIn: readonly number[]): string {
  return `aussi en ${alsoIn.join(', ')} : retirez l’un des deux pour lever le doute`;
}

/* ------------------------------------------------------------------- the noyau */

/** What the Noyau tab holds, the shape of the `set-noyau` operation without its operation. */
export type NoyauChoice = Pick<
  ReviewNoyau,
  'noyau' | 'certainty' | 'second' | 'unreadable' | 'note'
>;

/** Nothing posed yet. */
export const NO_NOYAU_CHOICE: NoyauChoice = {
  noyau: null,
  certainty: 'sure',
  second: null,
  unreadable: false,
  note: '',
};

/** The choice the review holds, or the empty one. */
export function noyauChoiceOf(noyau: ReviewNoyau | null): NoyauChoice {
  return noyau === null
    ? NO_NOYAU_CHOICE
    : {
        noyau: noyau.noyau,
        certainty: noyau.certainty,
        second: noyau.second,
        unreadable: noyau.unreadable,
        note: noyau.note,
      };
}

/** One of the six is chosen (§8): the « pas lisible » issue lifts, and a second equal to it goes. */
export function pickNoyau(current: NoyauChoice, code: NoyauCode): NoyauChoice {
  return {
    ...current,
    noyau: code,
    unreadable: false,
    second: current.second === code ? null : current.second,
  };
}

/** « Sûr » or « Avec doute »: a second candidate exists only with doubt. */
export function pickCertainty(current: NoyauChoice, certainty: ReviewCertainty): NoyauChoice {
  return { ...current, certainty, second: certainty === 'sure' ? null : current.second };
}

/** « Ou bien, peut-être »: pressing the second again lifts it. */
export function pickSecond(current: NoyauChoice, code: NoyauCode): NoyauChoice {
  return { ...current, second: current.second === code ? null : code };
}

/** « Pas de noyau lisible » empties the choice; pressed again, it only lifts itself. */
export function toggleUnreadable(current: NoyauChoice): NoyauChoice {
  return current.unreadable
    ? { ...current, unreadable: false }
    : { ...current, unreadable: true, noyau: null, second: null, certainty: 'sure' };
}

/** The operation that sets the noyau, or null if the choice is one the server would refuse. */
export function noyauOperation(
  choice: NoyauChoice,
): Extract<ReviewOperation, { op: 'set-noyau' }> | null {
  if (reviewNoyauProblem(choice) !== null) return null;
  return { op: 'set-noyau', ...choice };
}
